export const roles =  {
    DENTIST: 4,
    DENTIST_CLINIC: 3,
    ADMIN: 5,
    HYGIENIST: 6,
    ASSISTANT: 7,
    FRONT_DESK: 8,
    ASSOCIATE_DENTIST: 9,
} as const;

// Hygienist (talent) roles are 5, 6, 7, 8, 9
const HYGIENIST_ROLES = [roles.HYGIENIST, roles.ASSISTANT, roles.FRONT_DESK, roles.ASSOCIATE_DENTIST];
export type HygienistRole = typeof HYGIENIST_ROLES[number];

// Dental roles are 4, 3, 9
const DENTAL_ROLES = [roles.DENTIST, roles.DENTIST_CLINIC, roles.ASSOCIATE_DENTIST];
export type DentalRole = typeof DENTAL_ROLES[number];

export const JOB_STATUSES = {
    POSTED: 1,
    PARTIAL: 2,
    CONFIRMED: 3,
    COMPLETED: 4,
    OFFER_EXPIRED: 5
}

export const PARTIAL_OFFER_STATUS = {
    POSTED: 0,
    REJECTED: 1,
    ACCEPTED: 2,
    EXPIRED: 3,
    REMOVED: 4
}

export const SUPPORT_EMAIL= {
    CA: "help@tempstars.ca",
    US: "help@tempstars.com"
}

export const MARKETING_SOURCES: { value: string, label: string }[] = [
    { value: 'google', label: 'Google Search' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'friend', label: 'Friend/Colleague Referral' },
    { value: 'dental_association', label: 'Dental Association' },
    { value: 'conference', label: 'Industry Conference' },
    { value: 'other', label: 'Other' },
];

export const TALENT_MARKETING_SOURCES: { value: string, label: string }[] = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'google_ads', label: 'Google Ads' },
    { value: 'google_search', label: 'Google Search' },
    { value: 'word_of_mouth', label: 'Word of Mouth' },
    { value: 'other', label: 'Other' },
];

export const RELIABILITY_STATUSES = {
    '90': "Outstanding (Top 10%)",
    '80': "Very Good (Top 20%)",
    '70': "Good (Top 30%)",
    '60': "Fair (Top 40%)",
    '50': "Below Average (50%)"
}

export const RATINGS = {
    VERY_HAPPY: 5,
    PLEASED: 4.2,
    UNHAPPY: 2,
  }

  export const MINIMUM_PARTIAL_SHIFT_HOURS = 3;

  export const COUNTRY = {
    CA: 0,
    US: 1
  }

  export const LOCATION_IS_CENTRAL_BOOKED = {
    REGULAR: 0,
    DENTAL_CORP: 1,
    _123_DENTIST: 2,
  }

export enum HYGIENIST_TYPES {
  HYGIENIST = 0,
  ASSISTANT = 1,
  FRONT_DESK = 2,
  ASSOCIATE_DENTIST = 3,
}

export const HYGIENIST_SKILLS = [
    {
      id: 1,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Qualified & Trained Dental Assistant",
      name: "QTDA",
      description: "You are trained, have active qualifications and can work confidently & competently as a Dental Assistant in {{province}}",
      certified: "Learned on the job",
      qualification: false,
      license: true
    },
    {
      id: 2,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Certified Dental Assistant Level I (CDA I)",
      name: "CDA1",
      description: "You are a Certified Dental Assistant Level I in {{province}}.",
      certified: "Dental School",
      qualification: true,
      license: false
    },
    {
      id: 3,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Certified Dental Assistant Level II (CDA II)",
      name: "CDA2",
      description: "You are a Certified Dental Assistant Level II in {{province}}.",
      certified: "Dental School",
      qualification: true,
      license: false
    },
    {
      id: 4,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Certified Dental Assistant (CDA)",
      name: "CDA",
      description: "You are a Certified Dental Assistant with Formal Training in {{province}}.",
      certified: "Dental School",
      qualification: true,
      license: false
    },
    {
      id: 5,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Registered Dental Assistant (RDA)",
      name: "RDA",
      description: "You are a Registered Dental Assistant with formal training and certification in {{province}}",
      certified: "Dental School",
      qualification: true,
      license: false
    },
    {
      id: 6,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Dental Assistant (DA)",
      name: "DA",
      description: "You are an experienced and trained Dental Assistant, trained on-the-job but have no formal training or certification from an acreddited education institute in {{province}}",
      certified: "Learned on the job",
      qualification: true,
      license: true
    },
    {
      id: 7,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Certified or Formally Trained Dental Assistant",
      name: "CFTDA",
      description: "You are a Certified or Formally Trained Dental Assistant qualified to work in a dental office in {{province}}.",
      certified: "Dental School",
      qualification: true,
      license: false
    },
    {
      id: 8,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Expanded Function Dental Assistant",
      name: "EFDA",
      description: "You are a Certified and Qualified as an Expanded Function Dental Assistant in {{province}}.",
      certified: "Dental School",
      qualification: true,
      license: false
    },
    {
      id: 9,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Internationally Trained Dentist",
      name: "FTDA",
      description: "You have received training and qualification as a dentist outside of North America",
      certified: null,
      qualification: false,
      license: false
    },
    {
      id: 10,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Qualified & Trained to Take Dental X-rays",
      name: "TDX",
      description: "You are trained, certified, skilled and experienced at taking dental x-rays on patients in {{province}}.",
      certified: "Dental school (but if foreign-trained DA's receive this they can work as DA in North America)",
      qualification: false,
      license: false
    },
    {
      id: 11,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Certified for PPE, Stericentre and Infection Control",
      name: "PSIC",
      description: "You're trained & qualified for infection control protocols and sterilization centre duties in {{province}}",
      certified: "Dental School",
      qualification: false,
      license: false
    },
    {
      id: 12,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "CPR Certified and Current",
      name: "CPRC",
      description: "You are trained and certified in CPR and your qualifications are current and valid (not expired)",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 13,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Rubber Dam",
      name: "RD",
      description: "You're skilled and trained to place and remove rubber dam on dental patients.",
      certified: "Dental School",
      qualification: false,
      license: false
    },
    {
      id: 14,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Proph Polishing",
      name: "PP",
      description: "You're skilled, trained and qualified to independently provide prophy polishing on patients",
      certified: "Dental School",
      qualification: false,
      license: false
    },
    {
      id: 15,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Dental Impressions",
      name: "DI",
      description: "You're skilled, qualified and trained to independently take dental impressions (ie. alginate)",
      certified: "Dental School",
      qualification: false,
      license: false
    },
    {
      id: 16,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Coronal Scaling",
      name: "CS",
      description: "You are trained, certified, skilled and experienced at providing Coronal Scaling independently on dental patients in {{province}}.",
      certified: "Dental School",
      qualification: false,
      license: false
    },
    {
      id: 17,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Actively Licensed Registered Dental Hygienist (RDH)",
      name: "RDH",
      description: "You are formally trained, qualified and have an active RDH license to work as a Dental Hygienist in {{province}}",
      certified: "Dental School",
      qualification: false,
      license: true
    },
    {
      id: 18,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Internationally Trained Dentist",
      name: "FTD",
      description: "You have received training and qualification as a dentist outside of North America",
      certified: null,
      qualification: false,
      license: false
    },
    {
      id: 19,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Qualified & Trained to Take Dental X-rays",
      name: "TDX",
      description: "You are trained, certified, skilled and experienced at taking dental x-rays on patients in {{province}}",
      certified: "Dental School",
      qualification: false,
      license: false
    },
    {
      id: 20,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Certified for PPE, Stericentre and Infection Control",
      name: "PSIC",
      description: "You're trained & qualified for infection control protocols and sterilization centre duties in {{province}}",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 21,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Ultrasonic Scaler",
      name: "USC",
      description: "You're skilled and trained for Piezo/Cavitron ultrasonics",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 22,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "CPR Certified and Current",
      name: "CPRC",
      description: "You are trained and certified in CPR and your qualifications are current and valid (not expired)",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 23,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "AirFlow System",
      name: "AFS",
      description: "You're skilled and trained for AirFlow System Perio Care",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 24,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Chairside Whitening (Zoom, etc)",
      name: "CW",
      description: "You're skilled and trained to independently provide chairside whitening on patients",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 25,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Soft-tissue Perio Laser",
      name: "SPL",
      description: "You're skilled, trained and certified to independently provide soft tissue laser perio therapy",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 26,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Local Anaesthesia",
      name: "LAS",
      description: "You're skilled, certified and trained to independently administer local anaethesia in {{province}}.",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 27,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Restorative Hygienist",
      name: "RH",
      description: "You're skilled, certified and trained to independently provide restorative care on patients (filling and crown placements, etc.) in {{province}}",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 28,
      hygienistType: HYGIENIST_TYPES.HYGIENIST,
      label: "Orthdodontic Hygienist",
      name: "OH",
      description: "You're skilled and trained to independently provide orthodontic care on patients (place and remove bands, brackets, adjustments, etc.) in {{province}}",
      certified: "Additional Training",
      qualification: false,
      license: false
    },
    {
      id: 31,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Internationally Trained DDS (HARP)",
      name: "HFTDA",
      description: "You have received training and qualification as a dentist outside of North America and are HARP certified",
      certified: "Internationally trained",
      qualification: true,
      license: false
    },
    {
      id: 32,
      hygienistType: HYGIENIST_TYPES.ASSISTANT,
      label: "Internationally Trained DDS (No HARP)",
      name: "NHFTDA",
      description: "You have received training and qualification as a dentist outside of North America",
      certified: "Internationally trained",
      qualification: true,
      license: false
    },
] as const;

export type HygienistSkill = (typeof HYGIENIST_SKILLS)[number];
